import { SmartImage } from "@/components/ui/smart-image";
import { EventCategory } from "@/domain/event";
import { get } from "@/utils/utils";
import { motion, type MotionValue } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { CountdownFlipTimer } from "./countdown-flip-timer";

interface EventDetailTitleProps {
    eventImageUrl: string;
    eventTitle: string;
    compactProgress: MotionValue<number>;
    isSingleGuessDetailPage: boolean;
    isInset?: boolean;
    endTime?: number;
    eventCategory?: EventCategory;
}

/**
 * 事件详情页粘性标题
 * - 支持滚动渐变收缩
 * - 使用 transform: scale 整体缩放，避免布局变化导致的抖动
 */
export function EventDetailTitle( props: EventDetailTitleProps ) {
    const eventImageUrl = get( props, "eventImageUrl", "" );
    const eventTitle = get( props, "eventTitle", "" );
    const isSingleGuessDetailPage = get( props, "isSingleGuessDetailPage", false );
    const isInset = get( props, "isInset", false );
    const endTime = get( props, "endTime", undefined );
    const eventCategory = get( props, "eventCategory", EventCategory.Normal );
    const titleRef = useRef<HTMLDivElement | null>( null );
    const [ isTitleInView, setIsTitleInView ] = useState( true );
    const showCompactTitle = !isTitleInView;
    // 检测是不是区块链，如果是的话，并且如果是存在倒计时

    useEffect( () => {
        const target = titleRef.current;
        if ( !target ) return undefined;

        const observer = new IntersectionObserver( ( entries ) => {
            setIsTitleInView( entries[0]?.isIntersecting ?? true );
        }, { root: null, threshold: 0.1 } );

        observer.observe( target );
        return () => observer.disconnect();
    }, [ eventTitle, eventImageUrl ] );
    const showTime = useMemo( () => {
        if ( endTime ) {
            return endTime > Date.now();
        }
        return false;
    }, [ endTime ] );

    return (
        <>
            <motion.div
                className={`absolute z-40 bg-surface-card border-b border-stroke px-4 ${
                    isSingleGuessDetailPage ? "top-11 left-0 right-0 " : "top-0 -left-4 -right-4"
                }`}
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: showCompactTitle ? 1 : 0, y: showCompactTitle ? 0 : -12 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                style={{ pointerEvents: showCompactTitle ? "auto" : "none" }}
            >
                <div className="flex items-center gap-2 py-2">
                    <div className="rounded-md flex-shrink-0 overflow-hidden w-8 h-8">
                        <SmartImage
                            src={eventImageUrl}
                            alt={eventTitle}
                            className="w-full h-full object-cover"
                            imageUrlOptions={{ width: 150, height: 150 }}
                        />
                    </div>
                    <div
                        className="font-[600] text-t1 leading-tight text-[13px] flex-1 min-w-0"
                        style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {eventTitle}
                    </div>
                    {showTime && (
                        <div className="flex-shrink-0 self-start ">
                            <CountdownFlipTimer targetDate={endTime!} />
                        </div>
                    )}
                </div>
            </motion.div>

            <div ref={titleRef} className={`${isInset ? "-mx-4 px-4" : ""}`}>
                <div className="flex gap-3 items-center pt-3 pb-4 overflow-hidden">
                    <div className="rounded-lg flex-shrink-0 overflow-hidden w-12 h-12">
                        <SmartImage
                            src={eventImageUrl}
                            alt={eventTitle}
                            className="w-full h-full object-cover"
                            imageUrlOptions={{ width: 150, height: 150 }}
                        />
                    </div>
                    <h1
                        className={`font-[600] text-t1 leading-tight flex-1 min-w-0 line-clamp-3 ${
                            eventCategory === EventCategory.Crypto ? "text-base" : "text-xl"
                        }`}
                    >
                        {eventTitle}
                    </h1>
                    {showTime && (
                        <div className="flex-shrink-0 self-start ">
                            <CountdownFlipTimer targetDate={endTime!} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
